/**
 * The cheatsheet (`110-design-system.md` §7, roadmap M7 criterion 3).
 *
 * Rendered entirely from `keymap.ts`. It has no table of its own and it never will — the moment
 * this file contains a hard-coded row, the sheet can drift from the bindings, which is the exact
 * failure the criterion is written to catch ("a stale help overlay is worse than none").
 *
 * `?` opens it, and so does `Mod+/` (§7's own binding). Neither fires while a text field has
 * focus — `useHotkeys` owns that rule.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { KEYMAP, renderKeys } from '@/keyboard/keymap';

function Keys({ keys }: { keys: string }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {renderKeys(keys).map((chunk, index) =>
        chunk === 'then' ? (
          <span
            key={`${keys}-${String(index)}`}
            className="text-2xs text-[var(--muted-foreground)]"
          >
            then
          </span>
        ) : (
          <kbd
            key={`${keys}-${String(index)}`}
            className="rounded-sm border border-[var(--border)] bg-[var(--muted)] px-1.5 py-0.5 font-mono text-2xs whitespace-nowrap"
          >
            {chunk}
          </kbd>
        ),
      )}
    </span>
  );
}

export function ShortcutsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
        data-testid="shortcuts-dialog"
      >
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
        </DialogHeader>

        {/* Two columns on a roomy screen, one when there is not the width for it. Density is
            the point (§1.1): the whole map should be readable without scrolling if it can be. */}
        <div className="columns-1 gap-6 md:columns-2">
          {KEYMAP.map((section) => (
            <section key={section.title} className="mb-4 break-inside-avoid">
              <h3 className="mb-1 flex items-baseline gap-1.5 border-b border-[var(--border)] pb-1">
                <span className="text-xs font-semibold tracking-wide uppercase">
                  {section.title}
                </span>
                {section.where !== undefined && (
                  <span className="text-2xs font-normal text-[var(--muted-foreground)]">
                    {section.where}
                  </span>
                )}
              </h3>

              <dl className="flex flex-col">
                {section.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.keys + shortcut.action}
                    className="flex items-baseline justify-between gap-3 py-0.5"
                  >
                    <dt className="min-w-0">
                      <span>{shortcut.action}</span>
                      {shortcut.note !== undefined && (
                        <span className="ml-1.5 text-2xs text-[var(--muted-foreground)]">
                          {shortcut.note}
                        </span>
                      )}
                    </dt>
                    <dd>
                      <Keys keys={shortcut.keys} />
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
